import React, { useState } from 'react';
import { Box, Typography, Button, IconButton, Tabs, Tab, Menu, MenuItem } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { toast } from 'react-toastify';
import CreateBroadcastDialog from './CreateBroadcastDialog';
import EditBroadcastDialog from './EditBroadcastDialog';
import { marketingService, MarketingBroadcast } from '../../services/marketing.service';
import { NewPagination } from '../../components/Common/Pagination/Pagination';
import { useMarketingBroadcasts } from '../../hooks/useMarketingBroadcasts';

const Marketing: React.FC = () => {
  // Feature flag to show/hide banners section
  const showBanners = false; // Set to true to enable banners feature
  
  const [activeTab, setActiveTab] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState<MarketingBroadcast | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuBroadcastId, setMenuBroadcastId] = useState<string | null>(null);

  // Use the custom hook for pagination
  const {
    broadcasts,
    loading,
    currentPage,
    setCurrentPage,
    hasMore,
    totalPages,
    totalItems,
    pageStartIndex,
    pageEndIndex,
    refresh,
  } = useMarketingBroadcasts({ limit: 10 });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, broadcastId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuBroadcastId(broadcastId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuBroadcastId(null);
  };

  const handleEdit = () => {
    const broadcast = broadcasts.find(b => b.id === menuBroadcastId);
    if (broadcast) {
      setSelectedBroadcast(broadcast);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDuplicate = async () => {
    if (!menuBroadcastId) return;
    
    try {
      await marketingService.duplicateBroadcast(menuBroadcastId);
      toast.success('Broadcast duplicated successfully');
      refresh();
    } catch (error) {
      console.error('Failed to duplicate broadcast:', error);
      toast.error('Failed to duplicate broadcast');
    }
    handleMenuClose();
  };

  const handleSendNow = async () => {
    if (!menuBroadcastId) return;
    
    try {
      const result = await marketingService.sendBroadcastNow(menuBroadcastId);
      toast.success(`Broadcast sent to ${result.data.recipientCount} users`);
      refresh();
    } catch (error) {
      console.error('Failed to send broadcast:', error);
      toast.error('Failed to send broadcast');
    }
    handleMenuClose();
  };

  const handleCancelAndDelete = async () => {
    if (!menuBroadcastId) return;
    
    try {
      const broadcast = broadcasts.find(b => b.id === menuBroadcastId);
      
      if (broadcast?.status === 'SCHEDULED') {
        await marketingService.cancelBroadcast(menuBroadcastId);
      }
      
      await marketingService.deleteBroadcast(menuBroadcastId);
      toast.success('Broadcast deleted successfully');
      refresh();
    } catch (error) {
      console.error('Failed to delete broadcast:', error);
      toast.error('Failed to delete broadcast');
    }
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        padding: {
          xs: '24px 16px',
          sm: '40px 40px',
          md: '40px 80px',
        },
        gap: 'var(--palette-light-6-value)',
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--palette-light-4-value)' }}>
        <Typography sx={{ fontSize: 'var(--palette-light-font-size-h1-value)', fontWeight: 700, lineHeight: 1.15, color: 'var(--palette-light-text-primary-value-hex)' }}>
          Marketing
        </Typography>
        <Button
          variant="contained"
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
            color: 'var(--palette-light-text-foreground-value-hex)',
            textTransform: 'none',
            fontSize: 'var(--palette-light-font-size-default-value)',
            fontWeight: 700,
            borderRadius: 'var(--palette-light-4-value)',
            padding: '2px 32px 0',
            height: 'var(--palette-light-14-value)',
            '&:hover': {
              backgroundColor: 'var(--palette-light-brand-primary-value-hex)',
              opacity: 0.9,
            },
          }}
        >
          {showBanners && activeTab === 1 ? 'Create new banner' : 'New broadcast message'}
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--palette-light-8-value)' }}>
        {/* Tabs - Only show if banners feature is enabled */}
        {showBanners && (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: '39px',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTabs-flexContainer': {
                gap: 'var(--palette-light-2-value)',
              },
            }}
          >
            <Tab
              label="Broadcast messages"
              sx={{
                minHeight: '39px',
                padding: '10px 20px 7px',
                borderRadius: 'var(--palette-light-4-value)',
                textTransform: 'none',
                fontSize: 'var(--palette-light-font-size-default-value)',
                fontWeight: 400,
                color: 'var(--palette-light-text-primary-value-hex)',
                backgroundColor: activeTab === 0 ? 'var(--palette-light-brand-secondary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                '&.Mui-selected': {
                  color: 'var(--palette-light-text-foreground-value-hex)',
                },
              }}
            />
            <Tab
              label="Banners"
              sx={{
                minHeight: '39px',
                padding: '10px 20px 7px',
                borderRadius: 'var(--palette-light-4-value)',
                textTransform: 'none',
                fontSize: 'var(--palette-light-font-size-default-value)',
                fontWeight: 400,
                color: 'var(--palette-light-text-primary-value-hex)',
                backgroundColor: activeTab === 1 ? 'var(--palette-light-brand-secondary-value-hex)' : 'var(--palette-light-background-secondary-value-hex)',
                '&.Mui-selected': {
                  color: 'var(--palette-light-text-foreground-value-hex)',
                },
              }}
            />
          </Tabs>
        )}

        {/* Table - Always show broadcast messages (activeTab 0 or when banners disabled) */}
        {(activeTab === 0 || !showBanners) && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Table Header */}
            <Box
              sx={{
                display: 'flex',
                gap: 'var(--palette-light-4-value)',
                padding: '12px 0',
                borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  Message
                </Typography>
              </Box>
              <Box sx={{ width: '160px' }}>
                <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-secondary-value-hex)' }}>
                  Scheduled
                </Typography>
              </Box>
              <Box sx={{ width: '120px' }} />
              <Box sx={{ width: '56px' }} />
            </Box>

            {/* Table Rows */}
            {loading && broadcasts.length === 0 ? (
              <Box sx={{ padding: '40px', textAlign: 'center' }}>
                <Typography>Loading broadcasts...</Typography>
              </Box>
            ) : broadcasts.length === 0 ? (
              <Box sx={{ padding: '40px', textAlign: 'center' }}>
                <Typography>No broadcasts found</Typography>
              </Box>
            ) : (
              broadcasts.map((broadcast) => (
                <Box
                  key={broadcast.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--palette-light-4-value)',
                    padding: '11px 0',
                    borderBottom: '1px solid var(--palette-light-background-tertiary-value-hex)',
                  }}
                >
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 700, color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {broadcast.title}
                    </Typography>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {broadcast.message}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '160px' }}>
                    <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', color: 'var(--palette-light-text-primary-value-hex)' }}>
                      {new Date(broadcast.scheduledAt).toLocaleString('en-GB', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '120px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Box
                      sx={{
                        width: '100px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '32px',
                        borderRadius: 'var(--palette-light-2-value)',
                        backgroundColor: 
                          broadcast.status === 'SCHEDULED' ? 'var(--palette-light-status-success-value-hex)' :
                          broadcast.status === 'SENT' ? 'var(--palette-light-background-tertiary-value-hex)' :
                          broadcast.status === 'CANCELLED' ? 'var(--palette-light-status-warning-value-hex)' :
                          'var(--palette-light-status-danger-value-hex)',
                      }}
                    >
                      <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', color: 'var(--palette-light-text-foreground-value-hex)' }}>
                        {broadcast.status}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ width: '56px' }}>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, broadcast.id)}
                      sx={{
                        width: 'var(--palette-light-14-value)',
                        height: 'var(--palette-light-14-value)',
                        borderRadius: 'var(--palette-light-4-value)',
                        color: 'var(--palette-light-brand-primary-value-hex)',
                      }}
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        )}

        {/* Pagination */}
        {broadcasts.length > 0 && (
          <NewPagination
            currentPage={currentPage}
            totalPages={totalPages}
            hasNext={hasMore || currentPage < totalPages}
            onPageChange={setCurrentPage}
            itemsMessage={`${pageStartIndex}–${pageEndIndex} of ${totalItems}${hasMore ? '+' : ''} broadcasts`}
          />
        )}
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 'var(--palette-light-2-value)',
            boxShadow: '0px 40px 60px 0px rgba(26, 109, 109, 0.1)',
            mt: 'var(--palette-light-1-value)',
          },
        }}
      >
        <MenuItem onClick={handleEdit} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', padding: '12px 24px' }}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', padding: '12px 24px' }}>
          Duplicate
        </MenuItem>
        <MenuItem onClick={handleSendNow} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', padding: '12px 24px' }}>
          Send now
        </MenuItem>
        <MenuItem onClick={handleCancelAndDelete} sx={{ fontSize: 'var(--palette-light-font-size-default-value)', padding: '12px 24px', color: 'var(--palette-light-status-danger-value-hex)' }}>
          Cancel and delete
        </MenuItem>
      </Menu>

      {/* Dialogs */}
      <CreateBroadcastDialog 
        open={createDialogOpen} 
        onClose={() => {
          setCreateDialogOpen(false);
          refresh();
        }} 
      />
      {selectedBroadcast && (
        <EditBroadcastDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedBroadcast(null);
            refresh();
          }}
          broadcast={selectedBroadcast}
        />
      )}
    </Box>
  );
};

export default Marketing;
